import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { parse } from 'querystring';
import Alert from '@mui/material/Alert';
import { useEffect, useRef } from 'react';
import { orderConfirmation } from '../../../../layout/routes';
import {
  Feature,
  getFeatureByAddOn,
  PlanInterval,
  RecurlyAddOn,
  SubscriptionSource,
  trueSelfServeAddOn,
} from '../../../../models';
import { fromOrder, fromSubscription } from '../../../../store';
import { QueryDictionary, useNavigate, useQuery } from '../../../../routes';
import { formatCurrency } from '../../../../components';
import { useCalculatePrice, useGetTrackingCookies, usePlanCartSnapshot } from '../../../../hook';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { sendTrackingData } from '../../../../utils/utils';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../../utils/Constants';

const useStyles = makeStyles(theme => ({
  orderSummaryCard: {
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0, 2),
    margin: theme.spacing(0, 2),
    backgroundColor: '#ffffff',
    height: 'fit-content',
    position: 'sticky',
    top: theme.spacing(2),
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.75, 0),
  },
  summaryLabel: {
    fontWeight: 400,
    fontSize: 14,
    color: '#808B9A',
  },
  summaryValue: {
    fontWeight: 600,
    fontSize: 14,
    color: '#22283c',
  },
  summaryValueHighlight: {
    fontWeight: 700,
    fontSize: 14,
    color: '#5959EB',
  },
  savingsBanner: {
    backgroundColor: '#EEF2FF',
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    margin: theme.spacing(1.5, 0),
  },
  savingsBannerText: {
    fontWeight: 600,
    fontSize: 13,
    color: '#5959EB',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: theme.spacing(1),
  },
  totalLabel: {
    fontWeight: 700,
    fontSize: 16,
    color: '#22283c',
  },
  totalAmount: {
    fontWeight: 800,
    fontSize: 22,
    color: '#5959EB',
    textAlign: 'right',
  },
  billedLabel: {
    fontWeight: 400,
    fontSize: 12,
    color: '#808B9A',
    textAlign: 'right',
    marginBottom: theme.spacing(1),
  },
  checkoutButton: {
    backgroundColor: '#5959EB',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 15,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#43509B',
    },
    '&:disabled': {
      backgroundColor: '#b0b0b0',
      color: '#ffffff',
    },
  },
}));

export const OrderSummary = (
  { isAnnual }: {
    isAnnual: boolean;
  },
) => {
  const classes = useStyles();
  const intl = useIntl();
  const { get, queryString } = useQuery();
  const accountId = Number(get('accountId'));
  const { navigate, replaceQuery } = useNavigate();
  const { flags } = useLaunchDarkly();
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const subscription = useSelector(
    fromSubscription.selectTrueSubscriptions(SubscriptionSource.RECURLY),
  );
  const trackingCookies = useGetTrackingCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const previewCartRef = useRef<string | undefined>(undefined);
  const hasPendingOrder = pendingOrder && pendingOrder.length > 0;
  const productPreview = useSelector(fromOrder.selectOrderPreviewResponse)?.productsPreview?.[0];
  const seats = productPreview?.newQuantity ?? 0;
  const total = productPreview?.total ?? 0;
  const subTotal = productPreview?.subTotal ?? 0;
  const discount = productPreview?.discountAmount ?? 0;
  const addOns = productPreview?.lineItem.filter(
    it => Object.values(RecurlyAddOn).some(addOn => addOn === it.addOnCode)
      && it.addOnCode !== RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE,
  );
  const cloudQuota = productPreview?.lineItem.filter(
    it => trueSelfServeAddOn.some(addOn => addOn === it.addOnCode),
  ).reduce((sum, item) => sum + item.quantity, 0) ?? 0;

  const featureQuota: Partial<Record<Feature, number>> = {
    TRUE_TEAM: seats,
  };
  addOns?.forEach(item => featureQuota[getFeatureByAddOn(item.addOnCode)] = item.quantity);
  const monthlyPrice = useCalculatePrice(
    flags?.subscriptionPlanPrice,
    featureQuota,
    PlanInterval.MONTH,
  );
  const annualSavings = (monthlyPrice * 12) - total;

  const handleProceedToCheckout = () => {
    if (productPreview) {
      const navigateToCheckout = async () => {
        navigate(
          orderConfirmation.path,
          replaceQuery(parse(queryString()) as QueryDictionary),
        );
      };
      navigateToCheckout().catch(() => {
      });

      sendTrackingData(
        'payment_checkout_button_clicked',
        accountId,
        undefined,
        {
          field: 'plan_confirmation',
          object: 'button',
          action: 'proceed_to_checkout',
          currency: cartSnapshot.currency,
          value: cartSnapshot.value,
          items: cartSnapshot.items,
          ...trackingCookies,
        },
      );
    }
  };

  useEffect(() => {
    const serialized = JSON.stringify(cartSnapshot);
    if (cartSnapshot.items.length === 0
      || previewCartRef.current === serialized) {
      return;
    }

    previewCartRef.current = serialized;

    sendTrackingData(
      'preview_cart',
      accountId,
      undefined,
      {
        page: 'expand_plan',
        currency: cartSnapshot.currency,
        value: cartSnapshot.value,
        items: cartSnapshot.items,
        ...trackingCookies,
      },
    );
  }, [accountId, cartSnapshot]);

  return (
    <Paper className={classes.orderSummaryCard} elevation={0}>
      <Box mt={1.5}>
        <Box className={classes.summaryRow}>
          <Typography className={classes.summaryValue}>
            <FormattedMessage id="page.plan.v2.plan.title" />
          </Typography>
          <Typography className={classes.summaryValue}>
            <FormattedMessage
              id="page.plan.v2.order.summary.seats.value"
              values={{ count: seats }}
            />
          </Typography>
        </Box>
        <Box className={classes.summaryRow}>
          <Typography className={classes.billedLabel}>
            <FormattedMessage
              id={isAnnual
                ? 'page.plan.v2.order.summary.billed.annually'
                : 'page.plan.v2.order.summary.billed.monthly'}
            />
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

      {cloudQuota > 0 && (
        <>
          <Box className={classes.summaryRow}>
            <Typography className={classes.summaryLabel}>
              <FormattedMessage id="page.plan.v2.order.summary.cloud.sessions" />
            </Typography>
            <Typography className={classes.summaryValueHighlight}>
              <FormattedMessage
                id="page.plan.v2.order.summary.free.cloud.sessions.value"
                values={{ count: cloudQuota }}
              />
            </Typography>
          </Box>

          {addOns && addOns.map(lineItem => (
            <Box
              key={lineItem.addOnCode}
              className={classes.summaryRow}
              borderLeft="2px solid #e5e7eb"
            >
              <Typography color="#808B9A" fontWeight={400} fontSize={12} ml={1}>
                <FormattedMessage id={`addon.${lineItem.addOnCode}.display.name`} />
              </Typography>
              <Typography color="#5959EB" fontWeight={700} fontSize={12}>
                <FormattedMessage
                  id="page.plan.v2.order.summary.cloud.sessions.value"
                  values={{
                    quota: lineItem.quantity,
                  }}
                />
              </Typography>
            </Box>
          ))}

          <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
        </>
      )}

      <Box className={classes.summaryRow} pt={4} pb={1}>
        <Typography fontWeight={400} fontSize={14}>
          <FormattedMessage id="page.plan.v2.order.summary.standard.price" />
        </Typography>
        <Typography fontWeight={700} fontSize={14}>
          {formatCurrency(intl, subTotal, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
        </Typography>
      </Box>
      <Box className={classes.summaryRow} pt={1} pb={4}>
        <Typography fontWeight={400} fontSize={14}>
          <FormattedMessage id="page.plan.v2.order.summary.discount" />
        </Typography>
        <Typography fontWeight={700} fontSize={14}>
          {formatCurrency(intl, discount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
        </Typography>
      </Box>

      <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

      {!subscription && isAnnual && annualSavings > 0 && (
        <Box className={classes.savingsBanner}>
          <Typography className={classes.savingsBannerText}>
            🎉&nbsp;
            <FormattedMessage
              id="page.plan.v2.order.summary.savings"
              values={{
                amount: formatCurrency(intl, annualSavings, DEFAULT_CURRENCY_FORMAT_OPTIONS),
              }}
            />
          </Typography>
        </Box>
      )}

      <Box className={classes.totalRow} mt={1.5}>
        <Typography className={classes.totalLabel}>
          <FormattedMessage id="page.plan.v2.order.summary.total" />
        </Typography>
        <Box textAlign="right">
          <Typography className={classes.totalAmount}>
            {formatCurrency(intl, total, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
          </Typography>
        </Box>
      </Box>

      {hasPendingOrder && (
        <Box
          id="expand-plan-price-summary-alert"
          alignItems="center"
          alignContent="center"
          justifyContent="center"
        >
          <Alert
            id="expand-plan-price-summary-checkout-button-alert"
            severity="warning"
            sx={theme => ({
              marginBottom: 2,
              width: '100%',
              '& .MuiAlert-icon': {
                margin: theme.spacing(1, 2, 1, 0),
              },
            })}
          >
            <FormattedMessage id="page.expand.plan.price.summary.pending.order.alert" />
          </Alert>
        </Box>
      )}

      <Button
        fullWidth
        variant="contained"
        className={classes.checkoutButton}
        onClick={handleProceedToCheckout}
        disabled={!!hasPendingOrder}
      >
        <FormattedMessage id="page.plan.v2.checkout.button" />
      </Button>
    </Paper>
  );
};
